import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../../lib/utils"

// KaziPeople design-system Badge: pill, 11px / 600, soft tinted backgrounds.
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11px] font-semibold transition-colors focus:outline-none",
  {
    variants: {
      // ⚠️ ONE CLASS PER ROLE, NO dark: TWIN. A token already resolves to the
      // right value for the active theme, so a literal plus a dark: override is
      // two opinions where one will do — and the two had diverged: the light
      // half used real tokens (#FEF0E4 = --accent, #F0E8DC = --surface-sunken)
      // while the dark half reached for Tailwind's own palette (orange-300,
      // slate-300, white/10), which is not the KaziPeople ramp. The success /
      // warning / danger / info rows below were already right; the rest now
      // match them.
      //
      // purple and teal have no token because the design system has no purple
      // or teal. They keep literals, paired so the tint and its text come from
      // the same family, and are the two to delete or tokenise if a decision is
      // ever made about them.
      variant: {
        default: "bg-accent text-accent-foreground",
        orange: "bg-accent text-accent-foreground",
        success: "bg-success-soft text-success-strong",
        warning: "bg-warning-soft text-warning-strong",
        danger: "bg-danger-soft text-danger-strong",
        destructive: "bg-danger-soft text-danger-strong",
        info: "bg-info-soft text-info-strong",
        purple: "bg-[#EDE9FE] text-[#4C1D95] dark:bg-purple-500/15 dark:text-purple-300",
        teal: "bg-[#CCFBF1] text-[#115E59] dark:bg-teal-500/15 dark:text-teal-300",
        neutral: "bg-cream-dark text-charcoal-mid",
        secondary: "bg-cream-dark text-charcoal-mid",
        outline: "border border-border text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Render a small leading status dot in the badge's text color. */
  dot?: boolean
}

function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className="inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-current"
          aria-hidden="true"
        />
      )}
      {children}
    </div>
  )
}

export { Badge, badgeVariants }
